import React, { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { IconS24ArrowsCycle24, IconS24Plus24 } from '@is24/cosma-ui-icons'
import { SCOUT_MANAGER } from '../../../../../config'
import { useAppDispatch, useAppSelector } from '../../../../../app/hooks'
import { InitState } from '../../../../../redux/reducers/root/initialState'
import { DropdownOption, DropdownOptionSelection, OnFieldChangeType, ScreenProperty } from '../../../../../@types/types'
import ContactInfoListItemDetails from './ContactInfoListItemDetails'
import DropdownSelect from '../../../../common/inputs/dropdownSelect/DropdownSelect'
import { SCREEN_CONTACT_INFO, SCREENS } from '../../../../../services/domain/wizardScreens'
import { getContactsLoad } from '../../../../../redux/thunks/contacts/contacts'

export type ContactInfoManagerProps = {
  selectedContactId?: number
  onChange: OnFieldChangeType
}

const ContactInfoManager: React.FC<ContactInfoManagerProps> = ({ selectedContactId, onChange }) => {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()

  const contacts = useAppSelector((state: InitState) => state.contactFeature.contacts)

  const contactsAsOptions = useMemo(
    () =>
      [...contacts]
        .sort(
          (firstContact, secondContact) => Number(secondContact.defaultContact) - Number(firstContact.defaultContact)
        )
        .map((contact) => {
          const displayedName = `${contact.lastName ?? t('wizard.screen.contactInfo.manager.noName')}${
            contact.firstName ? ', ' + contact.firstName : ''
          } ${contact.defaultContact ? t('wizard.screen.contactInfo.manager.standardContact', 'Standard contact') : ''}`
          return {
            value: contact.contactId,
            label: displayedName,
          } as DropdownOption
        }),
    [contacts, t]
  )

  const contactDetails = (className: string, hasBorder: boolean) => (
    <div className={`grid-item one-whole desk-one-whole palm-margin-top lap-margin-top ${className}`}>
      <ContactInfoListItemDetails selectedContactId={selectedContactId} hasBorder={hasBorder} />
      <button
        aria-label={t('wizard.screen.contactInfo.manager.refresh')}
        className='grid-item align-center margin-top-m link-text palm-margin-vertical-m'
        onClick={() => dispatch(getContactsLoad())}
      >
        <IconS24ArrowsCycle24 inline />
        <span className='padding-left-xs'>{t('wizard.screen.contactInfo.manager.refresh')}</span>
      </button>
    </div>
  )

  return (
    <>
      <span className='font-body-medium-bold'>
        {t('wizard.screen.contactInfo.manager.subtitle', 'Contact person for this property')}
      </span>
      <a
        aria-label={t('wizard.screen.contactInfo.manager.addLabel')}
        className='margin-left-xxl desk-margin-left-xxl link-text padding-none palm-align-right float-right'
        target='_blank'
        rel='noopener noreferrer'
        href={SCOUT_MANAGER}
      >
        <IconS24Plus24 className='margin-right-xs' inline />
        <span className='palm-hide lap-hide'>{t('wizard.screen.contactInfo.manager.add')}</span>
      </a>
      <DropdownSelect
        id='contact-id'
        name='contactId'
        placeholder={contacts.length === 0 ? t('wizard.screen.contactInfo.manager.noContacts') : ''}
        value={selectedContactId}
        options={contactsAsOptions}
        errors={[]}
        onChange={(value: DropdownOptionSelection) =>
          onChange(
            { screen: SCREENS[SCREEN_CONTACT_INFO].name, property: 'contactId' } as ScreenProperty,
            Number(value.value)
          )
        }
        className={'dropdown-menu'}
      />
      {contactDetails('lap-hide desk-hide', false)}
      {contactDetails('palm-hide margin-top', true)}
    </>
  )
}

export default ContactInfoManager
